import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileEdit, Clock, User, AlertCircle, CheckCircle2, XCircle, MessageSquare } from 'lucide-react';
import { useNotify } from 'react-admin';
import {
  getChangeRequestById,
  approveChangeRequest,
  rejectChangeRequest,
  type ChangeRequest,
} from '../../../services/changeRequestService.ts';

const statusBadge = (status: ChangeRequest['status']) => {
  if (status === 'approved')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
        <CheckCircle2 size={14} /> Approved
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
        <XCircle size={14} /> Rejected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700">
      <Clock size={14} /> Pending
    </span>
  );
};

const fmt = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

export default function ChangeRequestView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  const [cr, setCr] = useState<ChangeRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState<'approve' | 'reject'>('approve');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const currentUserId = localStorage.getItem('userId') || '';

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getChangeRequestById(id);
      setCr(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load change request');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (action: 'approve' | 'reject') => {
    setModalAction(action);
    setComment('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!cr) return;
    if (modalAction === 'reject' && !comment.trim()) {
      notify('Rejection reason is required', { type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      if (modalAction === 'approve') {
        await approveChangeRequest(cr.id, comment || undefined);
        notify('Change request approved', { type: 'success' });
      } else {
        await rejectChangeRequest(cr.id, comment || undefined);
        notify('Change request rejected', { type: 'success' });
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      notify(e?.message || `Failed to ${modalAction}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !cr) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <div className="text-red-600 font-semibold mb-2">Failed to load change request</div>
        <div className="text-gray-600 mb-4">{error}</div>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          Go Back
        </button>
      </div>
    );
  }

  const isPending = cr.status === 'pending';
  const isAssignedApprover = String(cr.approverId) === String(currentUserId);
  const canAct = isPending && isAssignedApprover;

  return (
    <div className="px-4 pt-4 pb-6 w-full">
      {/* Header card */}
      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to List
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <FileEdit className="w-6 h-6 text-slate-600" />
            <div>
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">{cr.documentTitle}</h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm text-slate-500">{cr.documentNumber}</span>
                {cr.crNumber && (
                  <>
                    <span className="text-slate-300">|</span>
                    <span className="text-sm font-semibold text-slate-700">{cr.crNumber}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="mt-1">{statusBadge(cr.status)}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main */}
        <div className="col-span-2 space-y-5">
          {/* Change details */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Change Request Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500 mb-1">CR Number</div>
                <div className="text-gray-900 font-semibold">{cr.crNumber || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Document Number</div>
                <div className="text-blue-700 font-semibold">{cr.documentNumber}</div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Document Title</div>
                <div className="text-gray-900 font-medium">{cr.documentTitle}</div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Current Version</div>
                <div className="text-gray-900 font-medium">{cr.currentVersion || '-'}</div>
              </div>
              <div className="col-span-2">
                <div className="text-gray-500 mb-1">Change Reason</div>
                <div className="text-gray-900 whitespace-pre-wrap bg-slate-50 rounded-lg px-4 py-3 border border-slate-100">
                  {cr.changeReason}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1 flex items-center gap-1"><User size={13} /> Requested By</div>
                <div className="text-gray-900">{cr.requestedByName}</div>
              </div>
              <div>
                <div className="text-gray-500 mb-1 flex items-center gap-1"><Clock size={13} /> Requested At</div>
                <div className="text-gray-900">{fmt(cr.requestedAt)}</div>
              </div>
              <div>
                <div className="text-gray-500 mb-1 flex items-center gap-1"><User size={13} /> Assigned Approver</div>
                <div className="text-gray-900">{cr.approverName}</div>
              </div>
              {cr.resolvedAt && (
                <div>
                  <div className="text-gray-500 mb-1 flex items-center gap-1"><Clock size={13} /> Resolved At</div>
                  <div className="text-gray-900">{fmt(cr.resolvedAt)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Approver comment */}
          {cr.approverComment && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-gray-500" />
                <h3 className="text-base font-semibold text-gray-900">Approver Comment</h3>
              </div>
              <p className="text-gray-700 text-sm whitespace-pre-wrap">{cr.approverComment}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <h4 className="text-gray-900 font-semibold mb-3">Status</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Current</span>
                {statusBadge(cr.status)}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <h4 className="text-gray-900 font-semibold mb-3">Actions</h4>
            <div className="space-y-2">
              <button
                onClick={() => navigate(-1)}
                className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Back to List
              </button>
              {canAct && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    onClick={() => openModal('approve')}
                    className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-md transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Approve
                  </button>
                  <button
                    onClick={() => openModal('reject')}
                    className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Reject
                  </button>
                </div>
              )}
              {isPending && !isAssignedApprover && (
                <p className="text-xs text-slate-400 pt-1">Waiting for assigned approver</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve / Reject Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-xl">
            <div className={`flex items-center justify-between px-5 py-3 rounded-t-xl border-b ${modalAction === 'approve' ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
              <h2 className={`text-base font-bold ${modalAction === 'approve' ? 'text-emerald-800' : 'text-red-800'}`}>
                {modalAction === 'approve' ? 'Approve Change Request' : 'Reject Change Request'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm space-y-1.5">
                <div className="flex gap-2">
                  <span className="text-gray-500 w-28 shrink-0">Document</span>
                  <span className="font-medium text-gray-900">{cr.documentTitle}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500 w-28 shrink-0">Doc Number</span>
                  <span className="font-medium text-gray-900">{cr.documentNumber}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500 w-28 shrink-0">Change Reason</span>
                  <span className="font-medium text-gray-900">{cr.changeReason}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  {modalAction === 'approve' ? 'Comment (Optional)' : 'Rejection Reason (Required)'}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={modalAction === 'approve' ? 'Add your comment...' : 'Please provide rejection reason...'}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving || (modalAction === 'reject' && !comment.trim())}
                  className={`flex-1 px-4 py-2 text-sm rounded-lg text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    modalAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {saving ? 'Saving...' : modalAction === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
